import { Link } from "react-router-dom";
import { Award, GraduationCap, BookOpen, Users, Mic, Sparkles, MapPin } from "lucide-react";
import SectionHeader from "../components/site/SectionHeader";
import SafeImage from "../components/site/SafeImage";
import { CLINIC } from "../lib/clinic";
import { CLINIC_PHOTOS } from "../lib/clinicPhotos";
import { BOOK_SQUINT } from "../lib/books";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { breadcrumb, graph } from "../lib/schema";

const timeline = [
  {
    year: "2007",
    title: "All India PMT · AIR 50",
    where: "Entry to medicine",
    body: "Cleared the All India Pre-Medical Test (now NEET) with an outstanding All-India Rank of 50, inspired by his father Dr. Rajesh Gupta.",
  },
  {
    year: "2012",
    title: "MBBS",
    where: "University College of Medical Sciences (UCMS), New Delhi",
    body: "Graduated in medicine from one of India's most respected undergraduate medical colleges.",
  },
  {
    year: "NEET PG · AIR 158 / AIIMS PG · AIR 91",
    title: "Ophthalmology Residency Entry",
    where: "Guru Nanak Eye Centre · Maulana Azad Medical College",
    body: "Chose ophthalmology at GNEC/MAMC — India's most sought-after government super-speciality centre for eye training.",
  },
  {
    year: "MS",
    title: "Master of Surgery – Ophthalmology",
    where: "GNEC, MAMC, New Delhi",
    body: "Three-year post-graduate residency in comprehensive ophthalmology. Thesis awarded Best Poster at the American Academy of Ophthalmology, Chicago (2016).",
  },
  {
    year: "Senior Residency",
    title: "Cataract, Cornea & Medical Retina",
    where: "GNEC / MAMC, New Delhi",
    body: "Performed thousands of cataract surgeries, ~150 corneal transplants (with LCAT, SLET, OSSN excision), and a dedicated one-year rotation in medical retina — laser, FFA and intravitreal injections.",
  },
  {
    year: "DNB · FICO (Merit)",
    title: "Board Certifications",
    where: "Diplomate of National Board · Fellow of the International Council of Ophthalmology",
    body: "Cleared both DNB and FICO examinations with Merit during senior residency.",
  },
  {
    year: "Consultant",
    title: "Consultant Ophthalmologist",
    where: "Shantanu Netralaya Eye Hospital, Varanasi",
    body: "Practising at Shantanu Netralaya — the family eye hospital founded by his father Dr. Rajesh Gupta in 1990 — bringing New Delhi super-speciality training to eastern Uttar Pradesh.",
  },
];

const memberships = [
  "All India Ophthalmological Society (AIOS)",
  "Vitreo-Retinal Society of India (VRSI)",
  "Delhi Ophthalmological Society (DOS) – Alumnus",
  "International Council of Ophthalmology (ICO) – Fellow",
];

const About = () => {
  usePageMeta({
    title: "About Dr. Shantanu Kumar Gupta — MAMC, GNEC Trained Eye Specialist",
    path: "/about",
    description: "Ophthalmologist trained at Guru Nanak Eye Centre, MAMC, New Delhi. AAO Chicago Best Poster. Cataract, cornea & medical retina expertise in Varanasi.",
  });
  usePageJsonLd(graph([breadcrumb([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]));
  return (
    <div data-testid="about-page">
      <section className="pt-20 pb-16 border-b border-border">
        <div className="container-page grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <SafeImage
              src={CLINIC.doctorPortrait}
              alt="Dr. Shantanu Kumar Gupta — Cataract, Cornea & Medical Retina Specialist at Shantanu Netralaya Eye Hospital, Durgakund, Varanasi"
              label="Dr Shantanu"
              loading="eager"
              className="w-full aspect-[4/5] rounded-2xl border border-border"
              data-testid="about-portrait"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-4">About Dr. Shantanu Kumar Gupta</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance">
              New Delhi trained.
              <br /><span className="italic text-[hsl(var(--primary))]">Varanasi rooted.</span>
            </h1>
            <div className="mt-5 text-sm uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
              MBBS · MS (MAMC) · DNB · FICO (Merit)
            </div>
            <div className="mt-5 flex items-center gap-2 flex-wrap text-[11px] uppercase tracking-[0.18em]" data-testid="about-cross-domain">
              <span className="text-muted-foreground">Also on the web:</span>
              <a
                href="https://www.shantanunetralaya.com"
                target="_blank"
                rel="me noopener noreferrer"
                className="text-[hsl(var(--primary))] hover:underline underline-offset-2"
              >
                shantanunetralaya.com ↗
              </a>
              <span className="text-muted-foreground">·</span>
              <a
                href="https://www.researchgate.net/profile/Shantanu-Gupta-4"
                target="_blank"
                rel="me noopener noreferrer"
                className="text-[hsl(var(--primary))] hover:underline underline-offset-2"
              >
                ResearchGate ↗
              </a>
            </div>
            <div className="mt-8 space-y-5 prose-elegant">
              <p>
                Dr. Shantanu Kumar Gupta is a Varanasi-based ophthalmologist with a special interest in
                cataract surgery, cornea and medical retina. He completed his MBBS at the University
                College of Medical Sciences (UCMS), New Delhi, and his MS in Ophthalmology at the
                Guru Nanak Eye Centre (GNEC), Maulana Azad Medical College — India's most competitive
                government super-speciality centre for ophthalmology.
              </p>
              <p>
                Alongside an AIR 158 in NEET PG, he secured AIR 91 in the AIIMS PG entrance. His
                thesis was awarded <em>Best Poster</em> at the American Academy of Ophthalmology
                (AAO) conference in Chicago, 2016. He subsequently cleared both the DNB and the FICO
                (International Council of Ophthalmology) examinations with Merit.
              </p>
              <p>
                During his senior residency at GNEC, Dr. Shantanu performed thousands of cataract
                surgeries, close to 150 corneal transplant procedures, and completed a dedicated
                one-year rotation in medical retina — mastering laser therapy, fluorescein
                angiography and intravitreal injections. He was also trained in the planning and
                surgical aspects of LASIK and Phakic IOLs.
              </p>
              <p>
                Today he consults at <span className="font-medium">Shantanu Netralaya Eye Hospital</span>,
                the family-founded practice established by his father Dr. Rajesh Gupta in 1990,
                bringing New Delhi super-speciality training home to eastern Uttar Pradesh.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Journey timeline */}
      <section className="py-24 sm:py-28" data-testid="about-journey">
        <div className="container-page">
          <SectionHeader
            overline="Education & Training"
            title="A path forged in India's top eye centres."
            subtitle="From medical school to senior residency — a chronology of academic honours and clinical training."
          />
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {timeline.map((t) => (
              <div key={t.title} className="card-elegant p-8" data-testid={`journey-${t.title.replace(/\s/g, "-").toLowerCase().slice(0, 30)}`}>
                <div className="flex items-center gap-3 text-[hsl(var(--accent))]">
                  <GraduationCap size={18} strokeWidth={1.5} />
                  <span className="text-xs uppercase tracking-[0.2em]">{t.year}</span>
                </div>
                <h3 className="font-serif text-2xl mt-3">{t.title}</h3>
                <div className="text-sm text-muted-foreground mt-1">{t.where}</div>
                <p className="mt-4 text-sm leading-relaxed text-[hsl(var(--foreground))]/80">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards, memberships, research */}
      <section className="py-20 border-y border-border bg-[hsl(var(--secondary))]/40">
        <div className="container-page grid md:grid-cols-3 gap-12">
          <div>
            <Award size={30} strokeWidth={1.25} className="text-[hsl(var(--accent))]" />
            <h3 className="font-serif text-2xl mt-4">Honours & Awards</h3>
            <ul className="mt-4 space-y-3 text-sm text-[hsl(var(--foreground))]/80">
              <li>· <strong>Best Poster Award</strong> — American Academy of Ophthalmology (AAO), Chicago, 2016</li>
              <li>· <strong>FICO with Merit</strong> — International Council of Ophthalmology</li>
              <li>· <strong>AIR 50</strong> in All India PMT (now NEET)</li>
              <li>· <strong>AIR 91</strong> in AIIMS PG Entrance</li>
              <li>· <strong>AIR 158</strong> in NEET PG</li>
            </ul>
          </div>
          <div>
            <Users size={30} strokeWidth={1.25} className="text-[hsl(var(--accent))]" />
            <h3 className="font-serif text-2xl mt-4">Professional Memberships</h3>
            <ul className="mt-4 space-y-3 text-sm text-[hsl(var(--foreground))]/80">
              {memberships.map((m) => <li key={m}>· {m}</li>)}
            </ul>
          </div>
          <div>
            <BookOpen size={30} strokeWidth={1.25} className="text-[hsl(var(--accent))]" />
            <h3 className="font-serif text-2xl mt-4">Research & Presentations</h3>
            <ul className="mt-4 space-y-3 text-sm text-[hsl(var(--foreground))]/80">
              <li>
                · Editor · <Link to={`/book/${BOOK_SQUINT.slug}`} className="font-medium text-[hsl(var(--primary))] hover:underline" data-testid="about-book-editor-link">"{BOOK_SQUINT.fullTitle}"</Link> — patient guide by Dr. Manisha Mishra (Amazon KDP, 2026)
              </li>
              <li>· MS thesis · Best Poster · AAO Chicago 2016</li>
              <li>· Podium presentations at AIOS annual meetings</li>
              <li>· Contributed cases and case-series to peer literature</li>
              <li>· Active public education via YouTube & regional press</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Practice location */}
      <section className="py-20 container-page grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <MapPin size={30} strokeWidth={1.25} className="text-[hsl(var(--accent))]" />
          <h3 className="font-serif text-3xl mt-4">Where I Practise</h3>
          <p className="mt-4 prose-elegant">
            I consult and operate at <span className="font-medium">Shantanu Netralaya Eye Hospital</span> — a
            comprehensive multi-specialty eye hospital founded by my father Dr. Rajesh Gupta in 1990.
            Alongside our core team (Dr. Rajesh Gupta and Dr. Manisha Mishra), we offer cataract, cornea,
            retina, glaucoma, paediatric and squint services under one roof, empanelled with all leading
            insurance providers.
          </p>
          <div className="mt-6 text-sm text-muted-foreground">
            {CLINIC.address}
          </div>
        </div>

        <div>
          <Sparkles size={30} strokeWidth={1.25} className="text-[hsl(var(--accent))]" />
          <h3 className="font-serif text-3xl mt-4">Clinical Philosophy</h3>
          <p className="mt-4 prose-elegant italic">
            "Every eye tells a story. My job is to listen carefully, use technology wisely
            and recommend only what the evidence — and the patient's own life — actually
            needs."
          </p>
          <div className="mt-6 h-[2px] w-14 bg-[hsl(var(--accent))]" />
          <div className="mt-3 text-sm uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
            — Dr. Shantanu Kumar Gupta
          </div>
        </div>
      </section>

      {/* Teaching + Insurance */}
      <section className="py-20 border-t border-border">
        <div className="container-page grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <Mic size={30} strokeWidth={1.25} className="text-[hsl(var(--accent))]" />
            <h3 className="font-serif text-3xl mt-4">Teaching & Public Education</h3>
            <p className="mt-4 prose-elegant">
              Dr. Shantanu is a frequent speaker at community CMEs and mentors junior residents
              in cataract technique and medical retina. He anchors the "Ask The Eye Doctor" series
              on the Shantanu Netralaya YouTube channel — reaching thousands of patients every
              month with accurate, jargon-free eye education in English and Hindi.
            </p>
          </div>
          <div>
            <BookOpen size={30} strokeWidth={1.25} className="text-[hsl(var(--accent))]" />
            <h3 className="font-serif text-3xl mt-4">Insurance & Cashless Care</h3>
            <p className="mt-4 prose-elegant">
              At Shantanu Netralaya we are empanelled with leading insurers and TPAs — cashless
              treatment is available for eligible card-holders. We are also empanelled under
              <span className="font-medium"> Ayushman Bharat (PM-JAY)</span>, bringing quality eye
              care within reach of every patient.
            </p>
          </div>
        </div>

        {/* Empanelment posters */}
        <div className="container-page mt-16" data-testid="about-insurance-posters">
          <div className="grid md:grid-cols-2 gap-8">
            <figure className="rounded-2xl overflow-hidden border border-border bg-white">
              <SafeImage
                src={CLINIC_PHOTOS.insurancePoster}
                alt="Shantanu Netralaya — Cashless mediclaim facilities"
                label="Cashless Mediclaim"
                className="w-full aspect-[4/5]"
              />
            </figure>
            <figure className="rounded-2xl overflow-hidden border border-border bg-white">
              <SafeImage
                src={CLINIC_PHOTOS.ayushmanBharat}
                alt="Shantanu Netralaya empanelled under Ayushman Bharat PM-JAY"
                label="Ayushman Bharat PM-JAY"
                className="w-full aspect-[4/5]"
              />
            </figure>
          </div>
          <div className="mt-8 text-center">
            <Link to="/insurance" className="text-sm uppercase tracking-[0.22em] text-[hsl(var(--primary))] inline-flex items-center gap-2" data-testid="about-insurance-link">
              View full list of empanelled insurers <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
